<template>
  <section
    class="customcheckerboard-confirm-design"
  >
    <div class="customcheckerboard-confirm-design-body customcheckerboard-main-views-wrapper-body">
      <h5
        :class="[
          'customcheckerboard-confirm-design-title',
          'text-center',
          'customcheckerboard-main-views-wrapper-title',
          'header--no-text-transform',
        ]"
      >
          {{ translations.ugcConfirmDesc }}
      </h5>
      <img
        v-if="compositeImgKey"
        class="customcheckerboard-confirm-design-image"
        :src="imagesPreview[compositeImgKey].imgSrc"
        alt=""
        @load="onPreviewImageLoaded"
      />
    </div>
  </section>
</template>

<script>
import canvasUtils from "@/plugins/canvas/CanvasUtils";
import { warps } from '@/components/CustomCheckerboard/MainViews/WarpSelector/warpSettings';
import { checkerboardCombination } from '@/components/CustomCheckerboard/MainViews/SizeSelector/combinations';
import PlacementHelper from '../PlacementHelper';
import ugc from "@/mixins/UGC";
import undo from "@/mixins/Customizer/Undo";
import { getRegion, getLocale, getCaid, getConfigurationObject } from "@/data/regionInfo";

export default {
  name: "checkerboardConfirmDesign",
  extends: PlacementHelper,
  mixins:[ ugc, undo ],
  data() {
    return {
      compositeImgKey: null,
      imagesPreview: {},
      model: null,
      canvasConfig: null,
    };
  },
  props: {
    canvasLoaded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    translations(){
      return this.$store.state.translations.data;
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    useEcomDataLayerFormat() {
      return this.$store.getters['utils/useEcomDataLayerFormat'];
    }
  },
  watch: {
    canvasLoaded: function(newValue) {
      if (newValue) {
        this.generateConfirmImages();
      }
    }
  },
  methods: {
    onPreviewImageLoaded() {
      this.$store.commit("customCheckerboard/SET_ENABLE_CONTROLS", true);
    },
    getUgcConfirmDesignImages() {
      const featuredViews = this.pdm.product_images.featured_views.ugc_confirm_design;
      const keyNames = Object.keys(featuredViews);
      if (!keyNames.length) {
        console.error('PDM has no featured_views for ugc_confirm_design');
        return;
      }
      const firstKey = keyNames[0];
      const featuredView = featuredViews[firstKey];
      const recipe = this.$store.getters["ugc/tempData/getRecipe"];

      this.$store
        .dispatch("ugc/tempData/GET_PRODUCT_IMAGES_BY_RECIPE", {
          locale: getLocale(),
          recipe: recipe,
          images: featuredView,
          type: 'customcheckerboard',
        })
        .then(data => {
          this.processImages(data, featuredView);

          if (this.useEcomDataLayerFormat) {
            this.$checkerboardDataLayer.updateCategoryType(this.pdm.product_type);
            this.$checkerboardDataLayer.logAnalyticsEvent({
              eventLabel: this.$checkerboardDataLayer.labels.confirmDesignLoaded
            });
          } else {
            dataLayer.push({
              ugcStep: 'confirmDesignLoaded',
              timeStamp: new Date().toISOString(),
              event: "ugcStep",
              eventCategory:'customs',
              eventAction:'UGC',
              eventLabel: 'confirmDesignLoaded',
            });
          }
        })
        .catch(err => {
          console.error("Error getting product images by recipe:", err);
          this.$store.commit("customCheckerboard/SET_ENABLE_CONTROLS", true);
        });
    },

    createCompositeImages(featuredView) {
      this.imagesPreview = {};

      let compositeImages = {};

      for (let i = 0; i < featuredView.length; i++) {
        let composite = [];
        if (featuredView[i].size.includes("lg-")) {
          composite.push(featuredView[i]);
          if (featuredView[i].view.includes("comp")) {
            i = i + 1;
            composite.push(featuredView[i]);
          }
          const compositeKey = `composite_${i}`;
          compositeImages[compositeKey] = composite;

          this.imagesPreview[compositeKey] = {
            active: false,
            caption: compositeKey,
            imgSrc: ''
          };
        }
      }
      return compositeImages;
    },

    processImages(data, featuredView) {
      const { width, height } = this.pdm.product_images.renditions.square;
      const compositeImages = this.createCompositeImages(featuredView);
      const compositeKeys = Object.keys(compositeImages);

      Promise.all(
        compositeKeys.map(key => {

          const images = [];

          // first image that will be rendered in the background layer
          const backView = compositeImages[key][0];
          images.push(
            canvasUtils.getImageFromUrl(
              data[`${backView.view}_view`][backView.size]
            )
          );

          // second image that will be rendered on top of background layer
          if (compositeImages[key].length > 1) {
            const frontView = compositeImages[key][1];
            images.push(
              canvasUtils.getImageFromUrl(
                data[`${frontView.view}_view`][frontView.size]
              )
            );
          }

          return Promise.all(images)
            .then(([imageBuffer, imageOverlay]) => {
              return canvasUtils.applyImageOverlay({
                imageBuffer,
                imageOverlay,
                width: width,
                height: height
              });
            })
            .then(imageBase64 => {
              this.imagesPreview[key]['imgSrc'] = imageBase64.src;
            });
        })
      ).then(() => {
        const firstKey = compositeKeys[0];
        this.compositeImgKey = firstKey;
        this.imagesPreview[firstKey].active = true;
      });
    },

    getModelSize() {
      return this.$recipe.getUGCSizeFromRecipe() || this.pdm.ugcDefaultSize;
    },

    async generateConfirmImages() {
      // saves the generated image in the server
      const { components } = this.$store.state.customCheckerboard;
      const { tempData } = this.$store.state.ugc;
      let croppedImageDataUrl, ugcTemp;
      let index = 0;

      for (let component in components) {
        // This is needed since the first time it needs to use original recipe
        // the second time it needs to use the ugcTemp updated with the second component
        let recipe = index === 0 ? this.recipe_v2 : tempData.features[tempData.activeUGCIndex].recipe;
        const { canvasConfig: { warp, size } } = components[component];
        let { canvasConfig } = components[component];
        if (components[component].ugc && components[component].ugc.original_image) {
          ugcTemp =  components[component].ugc;
        } else {
          croppedImageDataUrl = await this.loadAndCropGeneratedImage(this.model, component, canvasConfig['warp']);
          ugcTemp = await this.generatePlacementAndUgcSwatch(croppedImageDataUrl, component, this.getModelSize());
          this.$store.commit('customCheckerboard/SET_COMPONENT_CONFIG', {
            component,
            canvasConfig,
            ugc: ugcTemp,
          });
        }
        this.updateTempRecipe(component, ugcTemp, recipe, warps[warp].id, checkerboardCombination[size][warp].swatchId);
        index++;
      }
      this.getUgcConfirmDesignImages();
    },

    async updateRecipe(isSecondComponentFlow) {
      const { components } = this.$store.state.customCheckerboard;
      for (let component in components) {
        const { canvasConfig: { warp, size } } = components[component];
        this.$recipe.addToolkitOptions(component, warps[warp].id, checkerboardCombination[size][warp].swatchId);
        this.$recipe.updateUGC(component, components[component]['ugc'], 'custom-checkerboard');
        await this.applyCheckerboardTo3D(component);
        this.$eventBus.$emit("block-features-by-swatch-id");
        this.$eventBus.$emit("update-menu-item-state", component);
      }

      await this.$store.dispatch('swatches/GET_RELATION_SWATCH_BY_FEATURE_ID', 'checkerboard')
        .then((response) => {
          this.$store.commit(
            'swatches/SET_DISABLED_SWATCHES_BY_FEATURE',
            { swatches: response.swatches_not_allowed, feature: 'customCheckerboard' },
          );

          if (isSecondComponentFlow) {
            this.$eventBus.$emit('blockRelationSwatches');
          }
        });
    },

    applyCheckerboardTo3D(componentName) {
      const component = this.recipe_v2.components[componentName];
      return this.applyUGCAsSwatch({ component: componentName, ugc: component.ugc });
    },

    applySavePreviousSwatch() {
      let saveComponents = [];

      for (const component in this.$store.state.customCheckerboard.components) {
        const feature = this.$recipe.getUGCFeatureType(component);

        this.$store.commit('swatches/SET_SWATCHES_SELECTED', {
          component: component,
          data: { ...this.selectedSwatches[component], default: false },
        });
        saveComponents.push(
          { component, feature, isSameFeature: feature === 'customCheckerboard' },
        );
      }

      if (this.$store.state.utils.configuration.enableFlyOutMenu) {
        this.updateStandardMenuForCustomCheckerboard();
      }

      this.savePreviousSwatch({ saveComponents });
    },

    async onFinishClicked() {
      const isSecondComponentFlow = this.$store.getters['customCheckerboard/isSecondComponentFlow'];
      let eventAction = 'checkerboard';

      if (isSecondComponentFlow) eventAction += ' - second component';
      if (this.useEcomDataLayerFormat) {
        this.$checkerboardDataLayer.updateCategoryType(this.pdm.product_type);
        this.$checkerboardDataLayer.logAnalyticsEvent({
          eventLabel: this.$checkerboardDataLayer.labels.finish
        });
      } else {
        dataLayer.push({
          'event': 'trackEvent',
          'eventCategory': 'customs',
          'eventAction': eventAction,
          'eventLabel': 'confirm design'
        });
      }
      this.$store.commit("customCheckerboard/SET_ENABLE_CONTROLS", false);
      this.applySavePreviousSwatch();
      await this.updateRecipe(isSecondComponentFlow);
      this.resetGeometrybyUGCFeature();
      this.$store.dispatch("buyPanel/GET_PRICE", { recipe: this.recipe_v2 });
      this.$store.commit("customCheckerboard/SET_ENABLE_CONTROLS", true);
      this.$modal.hide("customCheckerboard");
      this.$store.commit("customCheckerboard/SET_TEMP_COMPONENTS");
      // updates tabs
      this.$eventBus.$emit("block-features-by-swatch-id");
    },
  },
  created() {
    this.model = this.$store.state.utils.pdmModel;
    if (this.$props.canvasLoaded) {
      this.generateConfirmImages();
    }
    this.$eventBus.$on('customcheckerboard-finish-clicked', this.onFinishClicked);
  },
  beforeDestroy() {
    this.$eventBus.$off('customcheckerboard-finish-clicked', this.onFinishClicked);
  }
};
</script>
